import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  Users,
  Gamepad2,
  Clock,
  TrendingUp,
  Calendar,
  Filter,
  RefreshCw,
  Target,
  Brain,
  Trophy,
  Sparkles,
  CheckCircle,
  PlayCircle,
  AlertCircle,
} from 'lucide-react';
import api from '../services/api';
import { useAccessibility } from '../contexts/AccessibilityContext';

// ==================== INTERFACES ====================

interface Patient {
  id: number;
  identificador: string | null;
  first_name?: string;
  last_name?: string;
  username?: string;
  email: string;
}

interface Game {
  id: number;
  nombre: string;
  slug: string;
  icono?: string;
}

interface ActivityData {
  fecha: string;
  partidas: number;
}

interface GameProgressData {
  juego: string;
  partidas: number;
  icono?: string;
}

interface TopPatient {
  id: number;
  nombre: string;
  total_partidas: number;
  ultima_actividad: string;
}

interface SessionStats {
  total: number;
  finalizadas: number;
  en_curso: number;
  pendientes: number;
}

interface AdvancedStats {
  total_pacientes: number;
  total_partidas: number;
  tiempo_total_segundos: number;
  tiempo_promedio_segundos: number;
  actividad_30_dias: ActivityData[];
  progreso_por_juego: GameProgressData[];
  top_pacientes: TopPatient[];
  sesiones: SessionStats;
}

// ==================== COMPONENTE PRINCIPAL ====================

export default function ProfessionalStatsPage() {
  // Estados para datos
  const [stats, setStats] = useState<AdvancedStats | null>(null);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Estados para filtros
  const [selectedPatient, setSelectedPatient] = useState<string>('');
  const [selectedGame, setSelectedGame] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  // ==================== EFECTOS ====================

  useEffect(() => {
    loadInitialData();
  }, []);

  // ==================== FUNCIONES DE CARGA ====================

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Cargar datos en paralelo
      await Promise.all([
        loadStats(),
        loadPatients(),
        loadGames(),
      ]);
    } catch (err) {
      console.error('Error al cargar datos iniciales:', err);
      setError('Error al cargar los datos. Por favor, intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async (filters?: {
    paciente_id?: string;
    juego_id?: string;
    fecha_desde?: string;
    fecha_hasta?: string;
  }) => {
    try {
      const params = new URLSearchParams();
      if (filters?.paciente_id) params.append('paciente_id', filters.paciente_id);
      if (filters?.juego_id) params.append('juego_id', filters.juego_id);
      if (filters?.fecha_desde) params.append('fecha_desde', filters.fecha_desde);
      if (filters?.fecha_hasta) params.append('fecha_hasta', filters.fecha_hasta);

      const response = await api.get(`/professional/stats/advanced?${params.toString()}`);

      if (response.data.ok) {
        setStats(response.data.data);
      }
    } catch (err) {
      console.error('Error al cargar estadísticas:', err);
      throw err;
    }
  };

  const loadPatients = async () => {
    try {
      const response = await api.get('/professional/patients/list');
      if (response.data.ok) {
        setPatients(response.data.data);
      }
    } catch (err) {
      console.error('Error al cargar pacientes:', err);
    }
  };

  const loadGames = async () => {
    try {
      const response = await api.get('/games');
      if (response.data.ok) {
        setGames(response.data.data);
      }
    } catch (err) {
      console.error('Error al cargar juegos:', err);
    }
  };

  // ==================== MANEJADORES DE EVENTOS ====================

  const handleApplyFilters = async () => {
    try {
      setLoading(true);
      setError(null);

      await loadStats({
        paciente_id: selectedPatient || undefined,
        juego_id: selectedGame || undefined,
        fecha_desde: dateFrom || undefined,
        fecha_hasta: dateTo || undefined,
      });
    } catch (err) {
      setError('Error al aplicar filtros');
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = async () => {
    setSelectedPatient('');
    setSelectedGame('');
    setDateFrom('');
    setDateTo('');

    try {
      setLoading(true);
      setError(null);
      await loadStats();
    } catch (err) {
      setError('Error al limpiar filtros');
    } finally {
      setLoading(false);
    }
  };

  // ==================== FUNCIONES AUXILIARES ====================

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatTimeShort = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatDateShort = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
    });
  };

  const getGameIcon = (slug?: string) => {
    const iconMap: Record<string, any> = {
      bombillas: Target,
      hitori: Brain,
      kakurasu: Trophy,
      convergencia: Sparkles,
      default: Gamepad2,
    };
    return iconMap[slug || ''] || iconMap.default;
  };

  // ==================== COLORES PARA GRÁFICAS ====================

  const { highContrast } = useAccessibility();
  const COLORS = highContrast ? {
    primary: '#FFE600',
    secondary: '#00ccff',
    accent: '#ff6600',
    purple: '#ff00cc',
    pink: '#00ff88',
  } : {
    primary: '#10b981',
    secondary: '#3b82f6',
    accent: '#f59e0b',
    purple: '#a855f7',
    pink: '#ec4899',
  };

  // ==================== RENDER ====================

  if (loading && !stats) {
    return (
      <div className="min-h-screen py-8 px-4 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-neureka-yellow"></div>
          <p className="mt-4 text-white/70">Cargando estadísticas...</p>
        </div>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="min-h-screen py-8 px-4 flex items-center justify-center">
        <div className="glass-container text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Error</h2>
          <p className="text-white/70 mb-4">{error}</p>
          <button
            onClick={loadInitialData}
            className="btn-neureka"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="container mx-auto max-w-7xl">
        {/* ==================== HEADER ==================== */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-display font-bold mb-2">
            Estadísticas Avanzadas
          </h1>
          <p className="text-white/70 text-lg">
            Análisis detallado del rendimiento de tus pacientes
          </p>
        </motion.div>

        {/* ==================== FILTROS ==================== */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-container mb-8"
        >
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-neureka-yellow" />
            <h2 className="text-xl font-display font-bold">Filtros</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {/* Filtro de Paciente */}
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                Paciente
              </label>
              <select
                value={selectedPatient}
                onChange={(e) => setSelectedPatient(e.target.value)}
                className="input-neureka"
              >
                <option value="">Todos los pacientes</option>
                {patients.map((patient) => (
                  <option key={patient.id} value={patient.id.toString()}>
                    {patient.identificador || `${patient.first_name || ''} ${patient.last_name || ''}`.trim() || patient.username}
                  </option>
                ))}
              </select>
            </div>

            {/* Filtro de Juego */}
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                Juego
              </label>
              <select
                value={selectedGame}
                onChange={(e) => setSelectedGame(e.target.value)}
                className="input-neureka"
              >
                <option value="">Todos los juegos</option>
                {games.map((game) => (
                  <option key={game.id} value={game.id.toString()}>
                    {game.nombre}
                  </option>
                ))}
              </select>
            </div>

            {/* Filtro Fecha Desde */}
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                Fecha Desde
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="input-neureka"
              />
            </div>

            {/* Filtro Fecha Hasta */}
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                Fecha Hasta
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="input-neureka"
              />
            </div>
          </div>

          {/* Botones de acción */}
          <div className="flex gap-3">
            <button
              onClick={handleApplyFilters}
              disabled={loading}
              className="btn-neureka flex items-center gap-2"
            >
              <Filter className="w-4 h-4" />
              Aplicar Filtros
            </button>
            <button
              onClick={handleClearFilters}
              disabled={loading}
              className="btn-neureka-secondary flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Limpiar Filtros
            </button>
          </div>
        </motion.div>

        {/* ==================== SECCIÓN PARTIDAS ==================== */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="flex items-center gap-3 mb-4"
        >
          <Gamepad2 className="w-6 h-6 text-green-400" />
          <h2 className="text-2xl font-display font-bold">Partidas</h2>
          <span className="text-white/40 text-sm">juegos jugados libremente o en sesión</span>
        </motion.div>

        {/* ==================== STATS CARDS ==================== */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Pacientes */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass-container group hover:scale-105 transition-transform"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="p-3 rounded-lg bg-blue-500/10">
                <Users className="w-6 h-6 text-blue-400" />
              </div>
              <div className="text-3xl font-bold">
                {stats?.total_pacientes || 0}
              </div>
            </div>
            <h3 className="text-white/70 text-sm font-medium">
              Total Pacientes
            </h3>
          </motion.div>

          {/* Total Partidas */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="glass-container group hover:scale-105 transition-transform"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="p-3 rounded-lg bg-green-500/10">
                <Gamepad2 className="w-6 h-6 text-green-400" />
              </div>
              <div className="text-3xl font-bold">
                {stats?.total_partidas || 0}
              </div>
            </div>
            <h3 className="text-white/70 text-sm font-medium">
              Total Partidas
            </h3>
          </motion.div>

          {/* Tiempo Total */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass-container group hover:scale-105 transition-transform"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="p-3 rounded-lg bg-yellow-500/10">
                <Clock className="w-6 h-6 text-yellow-400" />
              </div>
              <div className="text-2xl font-bold">
                {formatTimeShort(stats?.tiempo_total_segundos || 0)}
              </div>
            </div>
            <h3 className="text-white/70 text-sm font-medium">
              Tiempo Total
            </h3>
          </motion.div>

          {/* Tiempo Promedio */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="glass-container group hover:scale-105 transition-transform"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="p-3 rounded-lg bg-purple-500/10">
                <TrendingUp className="w-6 h-6 text-purple-400" />
              </div>
              <div className="text-2xl font-bold">
                {formatTimeShort(stats?.tiempo_promedio_segundos || 0)}
              </div>
            </div>
            <h3 className="text-white/70 text-sm font-medium">
              Tiempo Promedio
            </h3>
          </motion.div>
        </div>

        {/* ==================== GRÁFICA DE ACTIVIDAD ==================== */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="glass-container mb-8"
        >
          <div className="flex items-center gap-2 mb-6">
            <Calendar className="w-5 h-5 text-neureka-yellow" />
            <h2 className="text-2xl font-display font-bold">
              Actividad en los últimos 30 días
            </h2>
          </div>

          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={stats?.actividad_30_dias || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis
                dataKey="fecha"
                tickFormatter={formatDateShort}
                stroke="rgba(255,255,255,0.5)"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="rgba(255,255,255,0.5)"
                style={{ fontSize: '12px' }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgba(0,0,0,0.8)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  color: 'white',
                }}
                labelFormatter={(label) => `Fecha: ${formatDate(label)}`}
                formatter={(value: any) => [`${value} partidas`, 'Partidas']}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="partidas"
                stroke={COLORS.primary}
                strokeWidth={2}
                dot={{ fill: COLORS.primary, r: 4 }}
                activeDot={{ r: 6 }}
                name="Partidas"
              />
            </LineChart>
          </ResponsiveContainer>
        </motion.div>

        {/* ==================== GRÁFICA DE PROGRESO POR JUEGO ==================== */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="glass-container mb-8"
        >
          <div className="flex items-center gap-2 mb-6">
            <Gamepad2 className="w-5 h-5 text-neureka-yellow" />
            <h2 className="text-2xl font-display font-bold">
              Partidas por Juego
            </h2>
          </div>

          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={stats?.progreso_por_juego || []}
              layout="vertical"
            >
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis
                type="number"
                stroke="rgba(255,255,255,0.5)"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                type="category"
                dataKey="juego"
                stroke="rgba(255,255,255,0.5)"
                style={{ fontSize: '12px' }}
                width={100}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgba(0,0,0,0.8)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  color: 'white',
                }}
                formatter={(value: any) => [`${value} partidas`, 'Partidas']}
              />
              <Legend />
              <Bar
                dataKey="partidas"
                fill={COLORS.secondary}
                radius={[0, 8, 8, 0]}
                name="Partidas"
              />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>

        {/* ==================== SECCIÓN SESIONES ==================== */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55 }}
          className="flex items-center gap-3 mb-4"
        >
          <Calendar className="w-6 h-6 text-blue-400" />
          <h2 className="text-2xl font-display font-bold">Sesiones</h2>
          <span className="text-white/40 text-sm">conjuntos de juegos asignados por el profesional</span>
        </motion.div>

        {/* ==================== GRID: TOP PACIENTES Y SESIONES ==================== */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Top 5 Pacientes */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="glass-container"
          >
            <div className="flex items-center gap-2 mb-6">
              <Trophy className="w-5 h-5 text-neureka-yellow" />
              <h2 className="text-2xl font-display font-bold">
                Top 5 Pacientes Más Activos
              </h2>
            </div>

            <div className="space-y-3">
              {stats?.top_pacientes && stats.top_pacientes.length > 0 ? (
                stats.top_pacientes.map((patient, index) => (
                  <Link
                    key={patient.id}
                    to={`/profesional/pacientes/${patient.id}`}
                    className="flex items-center gap-4 p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-colors group"
                  >
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-neureka-yellow to-yellow-400 flex items-center justify-center font-bold text-black">
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold truncate group-hover:text-neureka-yellow transition-colors">
                        {patient.nombre}
                      </h3>
                      <p className="text-sm text-white/60">
                        Última actividad: {formatDate(patient.ultima_actividad)}
                      </p>
                    </div>
                    <div className="flex-shrink-0 text-right">
                      <div className="text-2xl font-bold text-neureka-yellow">
                        {patient.total_partidas}
                      </div>
                      <div className="text-xs text-white/60">partidas</div>
                    </div>
                  </Link>
                ))
              ) : (
                <div className="text-center py-8 text-white/50">
                  No hay datos disponibles
                </div>
              )}
            </div>
          </motion.div>

          {/* Estadísticas de Sesiones */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="glass-container"
          >
            <div className="flex items-center gap-2 mb-6">
              <Calendar className="w-5 h-5 text-neureka-yellow" />
              <h2 className="text-2xl font-display font-bold">
                Estadísticas de Sesiones
              </h2>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Total Sesiones */}
              <div className="p-4 rounded-lg bg-gradient-to-br from-blue-500/20 to-blue-600/10 border border-blue-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-5 h-5 text-blue-400" />
                  <h3 className="text-sm font-medium text-white/70">
                    Total Sesiones
                  </h3>
                </div>
                <p className="text-3xl font-bold text-blue-400">
                  {stats?.sesiones?.total || 0}
                </p>
              </div>

              {/* Finalizadas */}
              <div className="p-4 rounded-lg bg-gradient-to-br from-green-500/20 to-green-600/10 border border-green-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <h3 className="text-sm font-medium text-white/70">
                    Finalizadas
                  </h3>
                </div>
                <p className="text-3xl font-bold text-green-400">
                  {stats?.sesiones?.finalizadas || 0}
                </p>
              </div>

              {/* En Curso */}
              <div className="p-4 rounded-lg bg-gradient-to-br from-purple-500/20 to-purple-600/10 border border-purple-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <PlayCircle className="w-5 h-5 text-purple-400" />
                  <h3 className="text-sm font-medium text-white/70">
                    En Curso
                  </h3>
                </div>
                <p className="text-3xl font-bold text-purple-400">
                  {stats?.sesiones?.en_curso || 0}
                </p>
              </div>

              {/* Pendientes */}
              <div className="p-4 rounded-lg bg-gradient-to-br from-yellow-500/20 to-yellow-600/10 border border-yellow-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-5 h-5 text-yellow-400" />
                  <h3 className="text-sm font-medium text-white/70">
                    Pendientes
                  </h3>
                </div>
                <p className="text-3xl font-bold text-yellow-400">
                  {stats?.sesiones?.pendientes || 0}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
